import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import momentDurationFormatSetup from 'moment-duration-format';
import ToggleSwitch from '@trendmicro/react-toggle-switch';

import PhoneInput from '../../FormComponents/PhoneInput';
import RangeSelect from '../../FormComponents/RangeSelect';
import TextareaCounter from '../../FormComponents/TextareaCounter';

momentDurationFormatSetup(moment);

export default function BusinessProfile(props) {
  const {
    errors,
    onBlur,
    onChange,
    setRef,
    disabled,
    getHandler,
    preferences,
  } = props;
  const { schema } = preferences;
  const { dental_preferences } = schema;
  const { general_information } = dental_preferences.properties;
  const values = Object.assign(
    {
      business_working_hours: {},
      business_open_days: {},
    },
    props.values,
  );

  const DaysOfWeek = [
    {
      label: 'Monday',
      code: 'MONDAY',
    },
    {
      label: 'Tuesday',
      code: 'TUESDAY',
    },
    {
      label: 'Wednesday',
      code: 'WEDNESDAY',
    },
    {
      label: 'Thursday',
      code: 'THURSDAY',
    },
    {
      label: 'Friday',
      code: 'FRIDAY',
    },
    {
      label: 'Saturday',
      code: 'SATURDAY',
    },
    {
      label: 'Sunday',
      code: 'SUNDAY',
    },
  ];

  const renderWorkingHours = (item, index) => {
    const onWorkingHoursChange = (range) => {
      let start;
      if (range[0] > 0 && range[0] !== 86400) {
        start = moment.duration(range[0], 's').format('HH:mm:ss');
      } else if (range[0] === 86400) {
        start = '23:59:59';
      } else {
        start = '00:00:00';
      }

      let end;
      if (range[1] > 0 && range[1] !== 86400) {
        end = moment.duration(range[1], 's').format('HH:mm:ss');
      } else if (range[1] === 86400) {
        end = '23:59:59';
      } else {
        end = '00:00:00';
      }

      onChange({
        target: {
          name: 'business_working_hours',
          value: Object.assign({}, values.business_working_hours, {
            [item.code]: {
              start,
              end,
            },
          }),
        },
      });
    };

    const onOpenDaysChange = () => {
      onChange({
        target: {
          name: 'business_open_days',
          value: Object.assign({}, values.business_open_days, {
            [item.code]: !values.business_open_days[item.code],
          }),
        },
      });
    };

    const { start, end } = values.business_working_hours[item.code] || {};
    const newValue = [];

    if (start && end) {
      newValue[0] = moment.duration(start, 'HH:mm:ss').asSeconds();
      newValue[1] = moment.duration(end, 'HH:mm:ss').asSeconds();
    }

    const defaultValue = [28800, 68400];

    return (
      <div className="multi-check__item multi-check__item--full" key={index}>
        <ToggleSwitch
          checked={!!values.business_open_days[item.code]}
          name={item.code}
          onChange={onOpenDaysChange}
          disabled={disabled}
          className="date-range-open"
        />
        <label htmlFor={item.code}>{item.label}</label>
        <div className="multi-check__item__slider" id={item.code}>
          <RangeSelect
            min={0}
            max={86400}
            defaultValue={defaultValue}
            value={newValue}
            step={1800}
            allowCross={false}
            marks={{ 0: '12:00am', 86400: '11:59pm' }}
            dots
            dotStyle={{
              width: '1px',
              border: '1px solid #e9e9e9',
              borderRadius: '0',
              bottom: '-8px',
              height: '4px',
              marginLeft: '-1px',
            }}
            tipFormatter={(value) => {
              let duration = moment.duration(value, 's').format('HH:mm');
              if (duration === '00') {
                duration = '00:00';
              } else if (duration === '30') {
                duration = '00:30';
              } else if (duration === '24:00') {
                duration = '23:59';
              }

              return moment(duration, 'HH:mm').format('hh:mma');
            }}
            disabled={!values.business_open_days[item.code] || disabled}
            onAfterChange={onWorkingHoursChange}
          />
        </div>
      </div>
    );
  };

  const onAcceptNewPatientsChange = () => {
    onChange({
      target: {
        type: 'checkbox',
        name: 'accept_new_patients',
        checked: !values.accept_new_patients,
      },
    });
  };

  return (
    <div className="business">
      {general_information?.properties?.website_link && (
        <div className="business__row">
          <p className="business__row__label">
            {general_information?.properties?.website_link?.description
              || 'Link to your website'}
          </p>
          <div className="business__row__input">
            <input
              type="text"
              name="website_link"
              value={values.website_link}
              onChange={onChange}
              className={classnames({ 'has-error': errors.website_link })}
              disabled={disabled}
              ref={setRef}
            />
            <span className="message--small message--error">
              {errors.website_link && errors.website_link.message}
            </span>
          </div>
        </div>
      )}
      {general_information?.properties?.faqs_link && (
        <div className="business__row">
          <p className="business__row__label">
            {general_information?.properties?.faqs_link?.description
             || 'Link to your FAQs'}
          </p>
          <div className="business__row__input">
            <input
              type="text"
              name="faqs_link"
              value={values.faqs_link}
              onChange={onChange}
              className={classnames({ 'has-error': errors.faqs_link })}
              disabled={disabled}
              ref={setRef}
            />
            <span className="message--small message--error">
              {errors.faqs_link && errors.faqs_link.message}
            </span>
          </div>
        </div>
      )}

      {general_information?.properties?.business_working_hours && (
        <div className="business__row business__row--column">
          <p className="business__row__label main-title">
            What are your working hours?
          </p>
          <div className="bookings__row__input multi-check">
            {DaysOfWeek.map(renderWorkingHours)}
          </div>
        </div>
      )}
      {general_information?.properties?.billing_contact_details && (
        <div className="mandatory__row">
          <p className="mandatory__row__label main-title less-padding">
            {general_information?.properties?.billing_contact_details?.description
              || 'Who should we contact for system/billing information?'}
            <sup>*</sup>
          </p>
          <div className="mandatory__row__input business-phone">
            <div className="label">FIRST AND LAST NAME</div>
            <input
              type="text"
              name="billing_contact_details_name"
              placeholder="Enter name"
              value={values.billing_contact_details_name}
              onChange={onChange}
              className={classnames({
                'has-error': errors['billing_contact_details_name'],
              })}
              ref={(c) => {
                setRef(c, 'billing_contact_details_name');
              }}
            />
            <span className="message--small message--error">
              {errors['billing_contact_details_name'] &&
                errors['billing_contact_details_name'].message}
            </span>
            <div className="label">EMAIL</div>
            <input
              type="email"
              name="billing_contact_details_email"
              placeholder="Enter email"
              value={values.billing_contact_details_email}
              onChange={onChange}
              className={classnames({
                'has-error': errors['billing_contact_details_email'],
              })}
              ref={(c) => {
                setRef(c, 'billing_contact_details_email');
              }}
            />
            <span className="message--small message--error">
              {errors['billing_contact_details_email'] &&
                errors['billing_contact_details_email'].message}
            </span>
            <div className="label">PHONE</div>
            <div
              className="business-phone"
              ref={(c) => {
                setRef(c, 'billing_contact_details.phone_number');
              }}
            >
              <PhoneInput
                placeholder="Enter phone number"
                name="billing_contact_details_phone"
                onChange={onChange}
                value={values.billing_contact_details_phone}
                className={classnames({
                  'has-error': errors['billing_contact_details_phone'],
                })}
              />
              <span className="message--small message--error">
                {errors['billing_contact_details_phone'] &&
                  errors['billing_contact_details_phone'].message}
              </span>
            </div>
          </div>
        </div>
      )}
      {general_information?.properties?.accept_new_patients && (
        <div className="business__row toggle-row toggle-center">
          <p className="business__row__label">
            {general_information?.properties?.accept_new_patients?.description
              || 'Are you accepting new patients at this time?'}
          </p>
          <div className="business__row__input custom-toggle">
            <ToggleSwitch
              checked={!!values.accept_new_patients}
              name="accept_new_patients"
              onChange={onAcceptNewPatientsChange}
              disabled={disabled}
            />
          </div>
        </div>
      )}
      {general_information?.properties?.age_restrictions && (
        <div className="business__row">
          <p className="business__row__label">
            {general_information?.properties?.age_restrictions?.description
              || 'How would you like TrueLark to respond if someone asks whether you accept children as patients?'}
          </p>
          <div className="business__row__input">
            <div>
              <TextareaCounter
                rows="3"
                name="age_restrictions"
                value={values.age_restrictions || ''}
                maxLength={160}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
      )}
      {general_information?.properties?.service_details && (
        <div className="business__row">
          <p className="business__row__label">
            {general_information?.properties?.service_details?.description
              || 'How would you like TrueLark to respond if a patient asks about specific procedures like RCTs, extractions, fillings, implants, etc.?'}
          </p>
          <div className="business__row__input">
            <div>
              <TextareaCounter
                rows="3"
                name="service_details"
                value={values.service_details || ''}
                maxLength={160}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
      )}
      {general_information?.properties?.service_details && (
        <div className="business__row">
          <p className="business__row__label">
            {general_information?.properties?.uniqueness?.description
              || 'How would you like TrueLark to answer if a patient asks what sets your dental practice apart from others?'}
          </p>
          <div className="business__row__input">
            <div>
              <TextareaCounter
                rows="3"
                name="uniqueness"
                value={values.uniqueness || ''}
                maxLength={160}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
      )}
      {general_information?.properties?.ease_patient_experience && (
        <div className="business__row">
          <p className="business__row__label">
            {general_information?.properties?.ease_patient_experience?.description
              || 'How would you like TrueLark to respond if a patient mentions feeling nervous or uncomfortable in a dental office?'}
          </p>
          <div className="business__row__input">
            <div>
              <TextareaCounter
                rows="3"
                name="ease_patient_experience"
                value={values.ease_patient_experience || ''}
                maxLength={160}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

BusinessProfile.defaultProps = {
  onChange: () => { },
  setRef: () => { },
  getHandler: () => { },
  values: {},
  errors: {},
  disabled: false,
};

BusinessProfile.propTypes = {
  onChange: PropTypes.func,
  setRef: PropTypes.func,
  getHandler: PropTypes.func,
  values: PropTypes.shape(),
  errors: PropTypes.shape(),
  disabled: PropTypes.bool,
};
